import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Nav from "../components/Nav";
import { CheckCircle, XCircle, ArrowRight } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const [attempts, setAttempts] = useState(0);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    stopRef.current = false;

    const poll = async (n = 0) => {
      if (stopRef.current) return;
      if (n >= 8) { setStatus("timeout"); return; }
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          refresh?.();
          return;
        }
        if (r.data.status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (e) {
        // continue polling
      }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { stopRef.current = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-paper">
      <Nav />
      <main className="max-w-2xl mx-auto px-6 py-20 text-center">
        {status === "polling" && (
          <>
            <div className="w-14 h-14 border-2 border-ikb border-t-transparent rounded-full animate-spin mx-auto" />
            <h1 className="font-display text-3xl mt-6">Confirming your payment…</h1>
            <p className="text-ink2 mt-3 font-mono-num text-xs">ATTEMPT {attempts + 1} / 8</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle weight="fill" size={64} className="text-pos mx-auto" />
            <h1 className="font-display text-4xl mt-6">You're on Pro.</h1>
            <p className="text-ink2 mt-3 max-w-md mx-auto">Unlimited AI questions and holdings are unlocked. Thanks for supporting StockProfit Life.</p>
            <button onClick={() => nav("/dashboard")} className="btn-primary mt-8" data-testid="payment-success-continue">
              Go to Dashboard <ArrowRight size={14} />
            </button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle weight="fill" size={64} className="text-neg mx-auto" />
            <h1 className="font-display text-3xl mt-6">
              {status === "timeout" ? "Still processing…" : "Payment not completed."}
            </h1>
            <p className="text-ink2 mt-3 max-w-md mx-auto">
              {status === "timeout"
                ? "Your payment is taking longer than expected. Refresh in a minute or contact support."
                : "The payment session was cancelled or expired. You can try again anytime."}
            </p>
            <button onClick={() => nav("/pricing")} className="btn-primary mt-8">
              Back to pricing <ArrowRight size={14} />
            </button>
          </>
        )}
      </main>
    </div>
  );
}
